import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Breadcrumbs,
  Divider,
  Grid,
  Skeleton,
  Chip,
  useTheme,
  IconButton,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { InfoOutlined } from '@mui/icons-material';
import { getServiceIaByIdStore } from 'store/services-ia';
import { StatusChip } from 'commons/generics/StatusChip';
import { InfoModal } from 'components/generics/InfoModal';

export const DetailServiciosIa = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();

  const [item, setItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const getModalContent = () => {
    switch (activeModal) {
      case 'provider':
        return {
          title: 'Proveedor',
          description: 'Proveedor de la API de IA.',
          infoText:
            'Ejemplo: Si seleccionas OpenAI, la conexión se hará con los servidores de OpenAI para utilizar sus modelos.',
        };
      case 'serviceType':
        return {
          title: 'Tipo de Servicio',
          description: 'El tipo de servicio que ofrece este modelo.',
          infoText: 'Ejemplo: Selecciona CHAT para configurar un asistente conversacional.',
        };
      case 'name':
        return {
          title: 'Nombre Comercial',
          description: 'Nombre descriptivo o comercial del modelo.',
          infoText:
            'Ejemplo: Asistente Avanzado (Gemini 1.5 Pro / GPT-4o) para que los usuarios lo identifiquen fácilmente.',
        };
      case 'code':
        return {
          title: 'Código Técnico (API)',
          description: 'El identificador exacto esperado por la API.',
          infoText: 'Ejemplo: gemini-1.5-pro o gpt-4o.',
        };
      case 'description':
        return {
          title: 'Descripción',
          description: 'Una breve descripción de lo que hace el modelo y sus casos de uso.',
          infoText: 'Ejemplo: Ideal para tareas que requieren razonamiento complejo.',
        };
      case 'currency':
        return {
          title: 'Moneda de Facturación',
          description: 'Moneda en la que se factura el uso del modelo.',
          infoText:
            'Ejemplo: USD, ya que la mayoría de proveedores internacionales facturan en dólares.',
        };
      case 'status':
        return {
          title: 'Estado Actual',
          description: 'Indica si el servicio está activo o inactivo en la plataforma.',
          infoText:
            'Ejemplo: Inactivo significa que los usuarios no podrán seleccionar ni utilizar este modelo temporalmente.',
        };
      case 'inputPrice':
        return {
          title: 'Costo Entrada (1M Tokens)',
          description: 'Costo por millón de tokens de entrada (Prompt).',
          infoText:
            'Ejemplo: 5.00 significa que cobrarán $5 USD por cada 1,000,000 de tokens enviados.',
        };
      case 'outputPrice':
        return {
          title: 'Costo Salida (1M Tokens)',
          description: 'Costo por millón de tokens de salida (Completion).',
          infoText:
            'Ejemplo: 15.00 significa que cobrarán $15 USD por cada 1,000,000 de tokens devueltos.',
        };
      case 'contextWindow':
        return {
          title: 'Ventana de Contexto',
          description: 'Tamaño máximo de la ventana de contexto en tokens.',
          infoText:
            'Ejemplo: 128000 para indicar que el modelo soporta aproximadamente 300 páginas de texto.',
        };
      case 'maxOutputTokens':
        return {
          title: 'Tokens Max. Salida',
          description: 'Cantidad máxima de tokens de salida permitidos.',
          infoText:
            'Ejemplo: 4096 es un límite común para asegurar respuestas completas pero controladas.',
        };
      case 'supportsStreaming':
        return {
          title: 'Streaming',
          description: 'Soporta respuestas en tiempo real (streaming).',
          infoText:
            'Ejemplo: El usuario verá aparecer el texto palabra por palabra en lugar de esperar a que se genere todo.',
        };
      case 'supportsTools':
        return {
          title: 'Herramientas (Tools)',
          description: 'Puede utilizar herramientas o funciones externas.',
          infoText:
            'Ejemplo: El modelo puede decidir llamar a una función para consultar la base de datos si necesita información específica.',
        };
      case 'supportsJson':
        return {
          title: 'Modo JSON',
          description: 'Soporta salida estructurada en formato JSON.',
          infoText:
            'Ejemplo: Se le puede pedir al modelo que devuelva la respuesta en un formato JSON estricto.',
        };
      case 'supportsVision':
        return {
          title: 'Visión (Imágenes)',
          description: 'Puede procesar y entender imágenes.',
          infoText:
            'Ejemplo: El modelo puede analizar una foto subida por el usuario y describir lo que contiene.',
        };
      case 'supportsAudio':
        return {
          title: 'Audio',
          description: 'Puede procesar entrada de audio.',
          infoText: 'Ejemplo: El modelo es capaz de recibir y transcribir notas de voz.',
        };
      case 'supportsRealtime':
        return {
          title: 'Tiempo Real',
          description: 'Soporta interacciones de voz/audio en tiempo real.',
          infoText:
            'Ejemplo: Para crear asistentes de voz que hablen y escuchen sin demoras notables.',
        };
      case 'supportsFunctionCall':
        return {
          title: 'Llamada a Funciones',
          description: 'Puede invocar funciones nativas.',
          infoText:
            'Ejemplo: El modelo detecta cuándo debe ejecutar una función del sistema basándose en la conversación.',
        };
      case 'supportsSystemPrompt':
        return {
          title: 'System Prompt',
          description: 'Acepta instrucciones de sistema separadas del mensaje del usuario.',
          infoText:
            'Ejemplo: Útil para darle una personalidad al asistente de forma independiente a la consulta del usuario.',
        };
      case 'supportsEmbeddings':
        return {
          title: 'Embeddings',
          description: 'Soporta la generación de embeddings.',
          infoText:
            'Ejemplo: El modelo puede convertir texto en vectores numéricos para búsquedas de similitud.',
        };
      case 'supportsCodeExecution':
        return {
          title: 'Ejecución de Código',
          description: 'Puede ejecutar código internamente (ej. Python).',
          infoText:
            'Ejemplo: El modelo puede generar y ejecutar código Python para resolver un cálculo matemático complejo.',
        };
      default:
        return { title: '', description: '', infoText: '' };
    }
  };
  const modalContent = getModalContent();

  const renderLabelWithInfo = (label: string, modalKey: string, color: string = '#696CFF') => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        variant="caption"
        sx={{ color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}
      >
        {label}
      </Typography>
      <IconButton
        size="small"
        color="info"
        onClick={() => setActiveModal(modalKey)}
        sx={{ ml: 0.5, p: 0.5 }}
      >
        <InfoOutlined fontSize="small" />
      </IconButton>
    </Box>
  );

  const renderCardLabelWithInfo = (label: string, modalKey: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <IconButton
        size="small"
        color="info"
        onClick={() => setActiveModal(modalKey)}
        sx={{ ml: 0.5, p: 0.5 }}
      >
        <InfoOutlined fontSize="small" />
      </IconButton>
    </Box>
  );

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    setLoading(true);
    const response = await dispatch(getServiceIaByIdStore(id));
    if (response?.ok && response?.data) {
      setItem(response.data);
    } else {
      navigate('/manager/plataforma-ia/configuracion/servicios');
    }
    setLoading(false);
  };

  if (loading)
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  if (!item) return null;

  const capabilities = [
    { key: 'supportsStreaming', label: 'Streaming' },
    { key: 'supportsTools', label: 'Herramientas (Tools)' },
    { key: 'supportsJson', label: 'Modo JSON' },
    { key: 'supportsVision', label: 'Visión (Imágenes)' },
    { key: 'supportsAudio', label: 'Audio' },
    { key: 'supportsRealtime', label: 'Tiempo Real' },
    { key: 'supportsFunctionCall', label: 'Llamada a Funciones' },
    { key: 'supportsSystemPrompt', label: 'System Prompt' },
    { key: 'supportsEmbeddings', label: 'Embeddings' },
    { key: 'supportsCodeExecution', label: 'Ejecución de Código' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AutoAwesomeIcon sx={{ color: '#696CFF' }} />
        <Typography
          fontSize={28}
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(45deg, #696CFF 30%, #A8B8FF 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Detalle del Modelo IA
        </Typography>
      </Box>

      <Breadcrumbs aria-label="breadcrumb" sx={{ paddingBottom: 2 }}>
        <Typography color="textPrimary" sx={{ fontWeight: 700 }}>
          Manager
        </Typography>
        <Link
          color="inherit"
          to="/manager/plataforma-ia/configuracion/servicios"
          style={{ textDecoration: 'none', color: theme.palette.text.secondary }}
        >
          Plataforma IA
        </Link>
        <Typography color="textPrimary">{item.name}</Typography>
      </Breadcrumbs>

      <Divider sx={{ marginBottom: 4 }} />

      <Grid container spacing={3}>
        {/* INFORMACIÓN BÁSICA */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              p: 4,
              height: '100%',
              borderRadius: 4,
              background:
                'linear-gradient(135deg, rgba(105,108,255,0.03) 0%, rgba(255,255,255,1) 100%)',
              boxShadow: '0px 10px 30px -10px rgba(105, 108, 255, 0.15)',
              border: '1px solid rgba(105,108,255,0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(105,108,255,0.1)',
                  display: 'flex',
                  mr: 2,
                  boxShadow: '0 0 15px rgba(105,108,255,0.2)',
                }}
              >
                <SmartToyIcon sx={{ color: '#696CFF', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#333' }}>
                  Identidad del Modelo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configuración central del motor de IA
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                {renderLabelWithInfo('Proveedor', 'provider')}
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  {item.provider}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {renderLabelWithInfo('Tipo', 'serviceType')}
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  {item.serviceType === 'CHAT' ? '🤖 ' : '⚙️ '}
                  {item.serviceType}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                {renderLabelWithInfo('Nombre Comercial', 'name')}
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, fontSize: '1.2rem', color: '#111' }}
                >
                  {item.name}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                {renderLabelWithInfo('Código Técnico (API)', 'code')}
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1,
                    backgroundColor: '#f1f5f9',
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    color: '#334155',
                  }}
                >
                  {item.code}
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                {renderLabelWithInfo('Descripción', 'description')}
                <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                  {item.description || 'Sin descripción disponible.'}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* COSTOS Y LÍMITES */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              p: 4,
              height: '100%',
              borderRadius: 4,
              background:
                'linear-gradient(135deg, rgba(0,210,255,0.03) 0%, rgba(255,255,255,1) 100%)',
              boxShadow: '0px 10px 30px -10px rgba(0, 210, 255, 0.15)',
              border: '1px solid rgba(0,210,255,0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(0,210,255,0.1)',
                  display: 'flex',
                  mr: 2,
                  boxShadow: '0 0 15px rgba(0,210,255,0.2)',
                }}
              >
                <DisplaySettingsIcon sx={{ color: '#00D2FF', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#333' }}>
                  Telemetría y Costos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Límites operativos y facturación de tokens
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                {renderLabelWithInfo('Moneda de Facturación', 'currency', '#00b0d6')}
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  {item.currency || 'USD'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {renderLabelWithInfo('Estado Actual', 'status', '#00b0d6')}
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip status={item.status} />
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    p: 2,
                    backgroundColor: '#f8fafc',
                    border: '1px dashed #cbd5e1',
                    boxShadow: 'none',
                  }}
                >
                  {renderCardLabelWithInfo('Costo Entrada (1M Tokens)', 'inputPrice')}
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    ${item.inputPrice ?? '0.00'}
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    p: 2,
                    backgroundColor: '#f8fafc',
                    border: '1px dashed #cbd5e1',
                    boxShadow: 'none',
                  }}
                >
                  {renderCardLabelWithInfo('Costo Salida (1M Tokens)', 'outputPrice')}
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    ${item.outputPrice ?? '0.00'}
                  </Typography>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {renderLabelWithInfo('Ventana de Contexto', 'contextWindow', '#00b0d6')}
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  {item.contextWindow
                    ? new Intl.NumberFormat('en-US').format(item.contextWindow) + ' tkns'
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {renderLabelWithInfo('Tokens Max. Salida', 'maxOutputTokens', '#00b0d6')}
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  {item.maxOutputTokens
                    ? new Intl.NumberFormat('en-US').format(item.maxOutputTokens) + ' tkns'
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* CAPACIDADES DEL MODELO */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              p: 4,
              borderRadius: 4,
              background:
                'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
              boxShadow: '0px 10px 30px -10px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e2e8f0',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                Capacidades Nativas del Modelo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Características soportadas oficialmente por la API
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {capabilities.map((cap) => {
                const isSupported = item[cap.key];
                return (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }} key={cap.key}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: isSupported ? 'rgba(93, 204, 10, 0.05)' : '#f8fafc',
                        border: isSupported
                          ? '1px solid rgba(93, 204, 10, 0.2)'
                          : '1px solid #f1f5f9',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        },
                      }}
                    >
                      {isSupported ? (
                        <CheckCircleIcon sx={{ color: '#5DCC0A', mr: 1.5 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#cbd5e1', mr: 1.5 }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSupported ? 700 : 500,
                          color: isSupported ? '#334155' : '#94a3b8',
                        }}
                      >
                        {cap.label}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setActiveModal(cap.key)}
                        sx={{ ml: 'auto' }}
                      >
                        <InfoOutlined
                          fontSize="small"
                          sx={{ color: isSupported ? '#5DCC0A' : '#94a3b8' }}
                        />
                      </IconButton>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Card>
        </Grid>
      </Grid>
      <InfoModal
        open={activeModal !== null}
        setOpen={(val: boolean) => {
          if (!val) setActiveModal(null);
        }}
        title={modalContent.title}
        description={modalContent.description}
        infoText={modalContent.infoText}
        icon="Info"
      />
    </Box>
  );
};
